/**
 * Apply attribution — remembers how a candidate reached a job so the
 * application can be credited to the right channel.
 *
 * Job links handed out by the employer's Broadcast panel carry
 * `?src=<channel>` (linkedin, whatsapp, jooble, …). A candidate usually logs
 * in or signs up between landing and applying, so the tag is stashed in
 * sessionStorage on arrival and read back when the apply form is submitted.
 *
 * Only a channel name and the referring site's address are kept — nothing
 * about the candidate — and only for the life of the tab.
 */

const STORAGE_KEY = 'sj_apply_source';

function readAll() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

function writeAll(value) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    /* storage unavailable (private mode) — attribution is best-effort */
  }
}

/** A referrer from our own site says nothing about the channel. */
function externalReferrer() {
  if (typeof document === 'undefined' || !document.referrer) return '';
  try {
    const refHost = new URL(document.referrer).hostname;
    const here = window.location.hostname;
    const apex = here.split('.').slice(-2).join('.');
    if (refHost === here || refHost === apex || refHost.endsWith(`.${apex}`)) return '';
    return document.referrer;
  } catch (e) {
    return '';
  }
}

/**
 * Record how the visitor arrived at `jobId`. An explicit tag on the URL
 * always wins; a bare referrer is only kept when nothing is stored yet, so
 * browsing around the site cannot erase the channel that brought them.
 */
export function captureApplySource(jobId) {
  if (typeof window === 'undefined' || !jobId) return;
  const params = new URLSearchParams(window.location.search);
  const tag = (params.get('src') || params.get('utm_source') || '').trim().slice(0, 32);
  const referrer = externalReferrer();

  const all = readAll();
  const key = String(jobId);
  if (tag) {
    all[key] = { source: tag, referrer };
  } else if (!all[key] && referrer) {
    all[key] = { source: '', referrer };
  } else {
    return;
  }
  writeAll(all);
}

/** `{ source, referrer }` for the apply form; empty strings when unknown. */
export function getApplySource(jobId) {
  const entry = readAll()[String(jobId)] || {};
  return { source: entry.source || '', referrer: entry.referrer || '' };
}
